import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { lessonService } from "../services/api";
import SideMenu from "./SideMenu";
import { useTheme } from '../ThemeContext';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import "./SideMenu.css";
import "./LessonAssessment.css";

// Add keyframes for spinner animation
const spinnerAnimation = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LessonAssessment = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [lesson, setLesson] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [score, setScore] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [isAssessmentGenerating, setIsAssessmentGenerating] = useState(false);

  // Add state for next lesson content generation tracking
  const [isNextLessonContentGenerating, setIsNextLessonContentGenerating] = useState(false);
  const [nextLessonContentStarted, setNextLessonContentStarted] = useState(false);
  const [showContentStatus, setShowContentStatus] = useState(false);
  
  // Add state to track payment/subscription errors
  const [hasPaymentError, setHasPaymentError] = useState(false);

  // Add an effect to synchronize with window.generatingAssessment
  useEffect(() => {
    // Create an interval to check window.generatingAssessment
    const checkWindowFlag = setInterval(() => {
      if (window.generatingAssessment) {
        setIsAssessmentGenerating(true);
      } else if (isAssessmentGenerating && !window.generatingAssessment) {
        setIsAssessmentGenerating(false);
      }
    }, 100);
    
    return () => clearInterval(checkWindowFlag);
  }, [isAssessmentGenerating]);
  
  // Initialize window.generatingAssessment on component mount
  useEffect(() => {
    // Reset the flag when component mounts
    if (window.generatingAssessment === undefined) {
      window.generatingAssessment = false;
    }
    
    // Clean up when component unmounts
    return () => {
      window.generatingAssessment = false;
    };
  }, []);

  // Effect to start content generation for next lesson
  useEffect(() => {
    const startNextLessonContentGeneration = async () => {
      if (!lessonId || nextLessonContentStarted || hasPaymentError) return;
      
      try {
        // Get the next lesson
        const nextLesson = await lessonService.getNextLesson(lessonId);
        
        if (nextLesson && nextLesson.id) {
          console.log(`Starting content generation for next lesson: ${nextLesson.id}`);
          setNextLessonContentStarted(true);
          await lessonService.startContentGeneration(nextLesson.id);
        } else {
          console.log('No next lesson found, skipping content generation');
        }
      } catch (error) {
        console.error('Error starting next lesson content generation:', error);
        
        // Check if it's a payment error (402)
        if (error.response && error.response.status === 402) {
          console.log('Payment required error detected, stopping next lesson content generation');
          setHasPaymentError(true);
        }
      }
    };

    // Start content generation after a short delay to let the assessment load first
    const timeoutId = setTimeout(startNextLessonContentGeneration, 2000);
    
    return () => clearTimeout(timeoutId);
  }, [lessonId, nextLessonContentStarted, hasPaymentError]);

  // Effect to monitor content generation status for next lesson
  useEffect(() => {
    if (!nextLessonContentStarted || !lessonId || hasPaymentError) return;
    
    const checkContentStatus = async () => {
      try {
        const nextLesson = await lessonService.getNextLesson(lessonId);
        if (nextLesson && nextLesson.id) {
          const isGenerating = lessonService.isContentGenerating(nextLesson.id);
          setShowContentStatus(isGenerating && !isNextLessonContentGenerating);
        }
      } catch (error) {
        // Check if it's a payment error (402)
        if (error.response && error.response.status === 402) {
          console.log('Payment required error detected in status checking, stopping monitoring');
          setHasPaymentError(true);
        }
        // Otherwise ignore errors in status checking
      }
    };
    
    // Check immediately
    checkContentStatus();
    
    // Check every 3 seconds
    const interval = setInterval(checkContentStatus, 3000);
    
    return () => clearInterval(interval);
  }, [nextLessonContentStarted, lessonId, isNextLessonContentGenerating, hasPaymentError]);

  useEffect(() => {
    const fetchLessonAndAssessment = async () => {
      try {
        if (!lessonId) {
          setError("No lesson ID provided");
          setLoading(false);
          return;
        }

        let assessmentLoaded = false;

        try {
          // Try to get an existing assessment for this lesson
          console.log(
            `Attempting to fetch existing assessment for lesson ID: ${lessonId}`
          );
          const assessmentData = await lessonService.getLessonAssessment(
            lessonId
          );

          if (assessmentData) {
            console.log(`Found existing assessment:`, assessmentData);
            setAssessment(assessmentData);
            assessmentLoaded = true;

            // Parse the questions from the assessment
            if (assessmentData.questions) {
              try {
                console.log(
                  "Parsing assessment questions:",
                  assessmentData.questions
                );

                // First try to parse if it's a string
                let parsedQuestions;
                if (typeof assessmentData.questions === "string") {
                  parsedQuestions = JSON.parse(assessmentData.questions);
                } else {
                  // Already parsed as an object
                  parsedQuestions = assessmentData.questions;
                }

                // Check and normalize the questions format
                if (
                  parsedQuestions.questions &&
                  Array.isArray(parsedQuestions.questions)
                ) {
                  console.log(
                    `Found ${parsedQuestions.questions.length} questions in standard format`
                  );
                  setQuestions(parsedQuestions.questions);
                } else if (Array.isArray(parsedQuestions)) {
                  console.log(
                    `Found ${parsedQuestions.length} questions in array format`
                  );
                  setQuestions(parsedQuestions);
                } else {
                  console.error(
                    "Unexpected questions format:",
                    parsedQuestions
                  );
                  setQuestions([]);
                  setError(
                    "Unable to parse assessment questions. Please try again later."
                  );
                  assessmentLoaded = false;
                }
              } catch (parseError) {
                console.error("Failed to parse questions JSON:", parseError);
                console.error("Raw questions data:", assessmentData.questions);
                setQuestions([]);
                setError(
                  "Unable to parse assessment questions. Please try again later."
                );
                assessmentLoaded = false;
              }
            } else {
              console.error(
                "No questions property in assessment data:",
                assessmentData
              );
              setError(
                "No questions were found for this assessment. Please try again later."
              );
              assessmentLoaded = false;
            }
          } else {
            console.log("No existing assessment found for this lesson");
            assessmentLoaded = false;
          }
        } catch (assessmentError) {
          console.error(
            "Failed to fetch existing assessment:",
            assessmentError
          );
          assessmentLoaded = false;
        }

        // If we couldn't load an existing assessment with valid questions, generate a new one
        if (!assessmentLoaded) {
          try {
            console.log("Generating new assessment for lesson:", lessonId);
            setError(""); // Clear any previous errors
            setIsAssessmentGenerating(true);

            // Add a flag to prevent duplicate assessment generation API calls
            if (!window.generatingAssessment) {
              window.generatingAssessment = true;
              const newAssessmentData =
                await lessonService.generateLessonAssessment(lessonId);
              window.generatingAssessment = false;
              console.log("Generated new assessment:", newAssessmentData);
              setAssessment(newAssessmentData);

              // Parse the questions from the new assessment
              if (newAssessmentData && newAssessmentData.questions) {
                try {
                  console.log(
                    "Parsing newly generated assessment questions:",
                    newAssessmentData.questions
                  );

                  // First try to parse if it's a string
                  let parsedQuestions;
                  if (typeof newAssessmentData.questions === "string") {
                    parsedQuestions = JSON.parse(newAssessmentData.questions);
                  } else {
                    // Already parsed as an object
                    parsedQuestions = newAssessmentData.questions;
                  }

                  // Check and normalize the questions format
                  if (
                    parsedQuestions.questions &&
                    Array.isArray(parsedQuestions.questions)
                  ) {
                    console.log(
                      `Found ${parsedQuestions.questions.length} questions in standard format`
                    );
                    setQuestions(parsedQuestions.questions);
                  } else if (Array.isArray(parsedQuestions)) {
                    console.log(
                      `Found ${parsedQuestions.length} questions in array format`
                    );
                    setQuestions(parsedQuestions);
                  } else {
                    console.error(
                      "Unexpected questions format in new assessment:",
                      parsedQuestions
                    );
                    setQuestions([]);
                    setError(
                      "Unable to parse assessment questions. Please try again later."
                    );
                  }
                } catch (parseError) {
                  console.error(
                    "Failed to parse new assessment questions JSON:",
                    parseError
                  );
                  console.error(
                    "Raw questions data:",
                    newAssessmentData.questions
                  );
                  setQuestions([]);
                  setError(
                    "Unable to parse assessment questions. Please try again later."
                  );
                }
              } else {
                console.error(
                  "No questions property in new assessment data:",
                  newAssessmentData
                );
                setError(
                  "No questions were generated for this assessment. Please try again later."
                );
              }
            } else {
              console.log('Assessment generation already in progress, skipping duplicate call');
            }
            setIsAssessmentGenerating(false);
          } catch (genError) {
            window.generatingAssessment = false;
            setIsAssessmentGenerating(false);
            console.error("Failed to generate assessment:", genError);
            const errorMessage =
              genError.response?.data ||
              "Failed to generate assessment questions";
            setError(errorMessage);
          }
        }

        setLoading(false);
      } catch (err) {
        setError("Failed to load lesson: " + (err.message || "Unknown error"));
        setLoading(false);
        console.error("Error loading lesson:", err);
      }
    };

    fetchLessonAndAssessment();
  }, [lessonId]);

  const handleAnswerChange = (questionIndex, answer) => {
    setAnswers({
      ...answers,
      [questionIndex]: answer,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Format answers as an array to match the backend expectation
      const answersArray = questions.map((question, index) => ({
        questionIndex: index,
        selectedAnswer: answers[index] || "",
        question: typeof question === "object" ? question.question : question,
      }));

      // Calculate the score based on correct answers if available
      let calculatedScore = 0;
      let totalQuestions = questions.length;

      if (totalQuestions > 0) {
        // If questions have correctAnswer property, use that to calculate the score
        if (
          typeof questions[0] === "object" &&
          "correctAnswer" in questions[0]
        ) {
          let correctCount = 0;

          questions.forEach((question, index) => {
            const userAnswer = answers[index];
            if (userAnswer === question.correctAnswer) {
              correctCount++;
            }
          });

          calculatedScore = Math.round((correctCount / totalQuestions) * 100);
        } else {
          // Otherwise just generate a random score between 60-100 as before
          calculatedScore = Math.floor(Math.random() * 41) + 60;
        }
      }

      // If we have an assessment ID, submit the answers
      if (assessment && assessment.id) {
        await lessonService.submitLessonAssessment(
          assessment.id,
          JSON.stringify(answersArray),
          calculatedScore
        );
      }

      setScore(calculatedScore);

      // If the score is 60% or higher, mark the lesson as completed
      if (calculatedScore >= 60 && lessonId) {
        console.log(
          `Score is ${calculatedScore}% (60% or higher). Marking lesson ${lessonId} as completed`
        );
        try {
          await lessonService.completeLesson(lessonId, calculatedScore);
          console.log(`Lesson ${lessonId} marked as completed successfully`);
          console.log(
            "Testing the implementation of the auto-generate lessons feature"
          );

          // Update the local lesson state to reflect completion
          setLesson({
            ...lesson,
            isCompleted: true,
            completedAt: new Date(),
          });
        } catch (completeError) {
          console.error(
            `Error marking lesson ${lessonId} as completed:`,
            completeError
          );
          // We don't need to show this error to the user since it doesn't affect their experience
        }
      }

      setSubmitting(false);
    } catch (err) {
      setError(
        "Failed to submit assessment: " + (err.message || "Unknown error")
      );
      setSubmitting(false);
      console.error("Error submitting assessment:", err);
    }
  };

  const handleContinue = async () => {
    try {
      // Use the new getNextLesson method
      const nextLesson = await lessonService.getNextLesson(lessonId);
      
      if (nextLesson) {
        // Check if content generation is in progress for the next lesson
        if (lessonService.isContentGenerating(nextLesson.id)) {
          setIsNextLessonContentGenerating(true);
          console.log('Next lesson content generation in progress, waiting for completion...');
          
          try {
            // Wait for content generation to complete
            await lessonService.waitForContentGeneration(nextLesson.id);
            console.log('Next lesson content generation completed, proceeding to lesson');
          } catch (error) {
            console.error('Content generation timeout or failed:', error);
            // Continue to next lesson anyway - it will handle generation there
          } finally {
            setIsNextLessonContentGenerating(false);
          }
        }
                  
        // Navigate to the next lesson
        navigate(`/lessons/${nextLesson.id}`);
      } else {
        // If there's no next lesson (this is the last one), go to learning plan
        navigate("/learning-plan");
      }
    } catch (error) {
      setIsNextLessonContentGenerating(false);
      console.error("Error finding next lesson:", error);
      
      // Check if it's a payment error (402)
      if (error.response && error.response.status === 402) {
        console.log('Payment required error detected, stopping navigation to next lesson');
        setHasPaymentError(true);
        // Don't navigate away, let user see the upgrade modal
        return;
      }
      
      // Fallback to learning plan if there's an error (but not payment error)
      navigate("/learning-plan");
    }
  };

  const handleReturnToLesson = async (e) => {
    // Format answers as an array to match the backend expectation
    const answersArray = questions.map((question, index) => ({
      questionIndex: index,
      selectedAnswer: answers[index] || "",
      question: typeof question === "object" ? question.question : question,
    }));

    // Calculate the score based on correct answers if available
    let calculatedScore = 0;
    let totalQuestions = questions.length;

    if (totalQuestions > 0) {
      // If questions have correctAnswer property, use that to calculate the score
      if (typeof questions[0] === "object" && "correctAnswer" in questions[0]) {
        let correctCount = 0;

        questions.forEach((question, index) => {
          const userAnswer = answers[index];
          if (userAnswer === question.correctAnswer) {
            correctCount++;
          }
        });

        calculatedScore = Math.round((correctCount / totalQuestions) * 100);
      } else {
        // Otherwise just generate a random score between 60-100 as before
        calculatedScore = Math.floor(Math.random() * 41) + 60;
      }
    }

    // If we have an assessment ID, submit the answers
    if (assessment && assessment.id) {
      await lessonService.submitLessonAssessment(
        assessment.id,
        JSON.stringify(answersArray),
        calculatedScore
      );
    }

    setScore(calculatedScore);

    // If the score is 60% or higher, mark the lesson as completed
    if (calculatedScore >= 60 && lessonId) {
      console.log(
        `Score is ${calculatedScore}% (60% or higher). Marking lesson ${lessonId} as completed`
      );
      try {
        await lessonService.completeLesson(lessonId, calculatedScore);
        console.log(`Lesson ${lessonId} marked as completed successfully`);
        console.log(
          "Testing the implementation of the auto-generate lessons feature"
        );

        // Update the local lesson state to reflect completion
        setLesson({
          ...lesson,
          isCompleted: true,
          completedAt: new Date(),
        });
      } catch (completeError) {
        console.error(
          `Error marking lesson ${lessonId} as completed:`,
          completeError
        );
        // We don't need to show this error to the user since it doesn't affect their experience
      }
    }

    if (lessonId) {
      navigate(`/lessons/${lessonId}`);
    } else {
      // Fallback to home if no lesson ID
      navigate("/home");
    }
  };

  // Handler for regenerating the assessment
  const handleRegenerateAssessment = async () => {
    try {
      setRegenerating(true);
      setError("");
      setQuestions([]);
      setIsAssessmentGenerating(true);
      
      console.log(`Regenerating assessment for lesson ID: ${lessonId}`);
      
      // Use the regenerateAssessment service method and prevent duplicate calls
      if (!window.generatingAssessment) {
        window.generatingAssessment = true;
        const newAssessmentData = await lessonService.regenerateAssessment(lessonId);
        window.generatingAssessment = false;
        
        console.log("Regenerated assessment:", newAssessmentData);
        setAssessment(newAssessmentData);
        
        // Reset answers
        setAnswers({});
        
        // Parse the questions from the regenerated assessment
        if (newAssessmentData && newAssessmentData.questions) {
          try {
            console.log(
              "Parsing regenerated assessment questions:",
              newAssessmentData.questions
            );
            
            // First try to parse if it's a string
            let parsedQuestions;
            if (typeof newAssessmentData.questions === "string") {
              parsedQuestions = JSON.parse(newAssessmentData.questions);
            } else {
              // Already parsed as an object
              parsedQuestions = newAssessmentData.questions;
            }
            
            // Check and normalize the questions format
            if (
              parsedQuestions.questions &&
              Array.isArray(parsedQuestions.questions)
            ) {
              console.log(
                `Found ${parsedQuestions.questions.length} questions in standard format`
              );
              setQuestions(parsedQuestions.questions);
            } else if (Array.isArray(parsedQuestions)) {
              console.log(
                `Found ${parsedQuestions.length} questions in array format`
              );
              setQuestions(parsedQuestions);
            } else {
              console.error(
                "Unexpected questions format in regenerated assessment:",
                parsedQuestions
              );
              setQuestions([]);
              setError(
                "Unable to parse assessment questions. Please try again later."
              );
            }
          } catch (parseError) {
            console.error(
              "Failed to parse regenerated assessment questions JSON:",
              parseError
            );
            console.error(
              "Raw questions data:",
              newAssessmentData.questions
            );
            setQuestions([]);
            setError(
              "Unable to parse assessment questions. Please try again later."
            );
          }
        } else {
          console.error(
            "No questions property in regenerated assessment data:",
            newAssessmentData
          );
          setError(
            "No questions were generated for this assessment. Please try again later."
          );
        }
      } else {
        console.log('Assessment regeneration already in progress, skipping duplicate call');
      }
    } catch (error) {
      window.generatingAssessment = false;
      console.error("Error regenerating assessment:", error);
      setError(`Failed to regenerate assessment: ${error.message || "Unknown error"}`);
    } finally {
      setRegenerating(false);
      setIsAssessmentGenerating(false);
    }
  };

  const openRegenerateModal = () => {
    setShowRegenerateModal(true);
  };

  const closeRegenerateModal = () => {
    setShowRegenerateModal(false);
  };

  const handleRegenerateClick = () => {
    openRegenerateModal();
  };

  const handleConfirmRegenerate = async () => {
    closeRegenerateModal();
    setRegenerating(true);
    try {
      await handleRegenerateAssessment();
    } finally {
      setRegenerating(false);
    }
  };

  // Helper function to render math content and handle markdown
  const renderMathContent = (content) => {
    if (!content) return '';
    
    let processedContent = content;
    
    // Handle display math with \[ ... \]
    processedContent = processedContent.replace(/\\\[([\s\S]*?)\\\]/g, (match, formula) => {
      try {
        return katex.renderToString(formula.trim(), {
          displayMode: true,
          throwOnError: false,
          strict: false,
          output: 'html',
          macros: {
            "\\R": "\\mathbb{R}",
            "\\N": "\\mathbb{N}"
          }
        });
      } catch (error) {
        console.error('KaTeX rendering error:', error, 'for content:', formula);
        return `<span class="math-error">Error rendering math: ${error.message} for ${formula.substring(0, 30)}...</span>`;
      }
    });
    
    // Handle inline math with \( ... \)
    processedContent = processedContent.replace(/\\\(([\s\S]*?)\\\)/g, (match, formula) => {
      try {
        return katex.renderToString(formula.trim(), {
          displayMode: false,
          throwOnError: false,
          strict: false,
          output: 'html',
          macros: {
            "\\R": "\\mathbb{R}",
            "\\N": "\\mathbb{N}"
          }
        });
      } catch (error) {
        console.error('KaTeX rendering error:', error, 'for content:', formula);
        return `<span class="math-error">Error rendering math: ${error.message} for ${formula.substring(0, 30)}...</span>`;
      }
    });
    
    // Handle LaTeX environments like \begin{...} ... \end{...}
    processedContent = processedContent.replace(/\\begin\{([^}]+)\}([\s\S]*?)\\end\{\1\}/g, (match) => {
      try {
        return katex.renderToString(match, {
          displayMode: true,
          throwOnError: false,
          strict: false,
          output: 'html',
          macros: {
            "\\R": "\\mathbb{R}",
            "\\N": "\\mathbb{N}"
          }
        });
      } catch (error) {
        console.error('KaTeX rendering error:', error, 'for content:', match);
        return `<span class="math-error">Error rendering math: ${error.message} for ${match.substring(0, 30)}...</span>`;
      }
    });
    
    return processedContent;
  };

  // Update the display of questions to render math
  const renderQuestion = (question) => {
    // Get the question text based on its structure
    const questionText = typeof question === "object" ? question.question : question;
    
    // If the question contains math delimiters, render it with KaTeX
    if (questionText && (questionText.includes('\\[') || questionText.includes('\\(') || questionText.includes('\\begin{'))) {
      return <p className="question-text" dangerouslySetInnerHTML={{ __html: renderMathContent(questionText) }} />;
    }
    
    // Otherwise, render as plain text
    return <p className="question-text">{questionText}</p>;
  };

  // Update the display of options to render math
  const renderOption = (option) => {
    // If the option contains math delimiters, render it with KaTeX
    if (option && (option.includes('\\[') || option.includes('\\(') || option.includes('\\begin{'))) {
      return <span className="option-text" dangerouslySetInnerHTML={{ __html: renderMathContent(option) }} />;
    }
    
    // Otherwise, render as plain text
    return <span className="option-text">{option}</span>;
  };

  if (loading) {
    return (
      <div className="app-layout">
        <SideMenu />
        <div className={`learning-plan-main-content ${theme}`}>
          <div className="sb-loading-container">
            <div className="loading-icon-container">
              <div className="empty-state-icon">📝</div>
            </div>
            <div className="sb-loading-row">
              <div className="sb-loading-spinner"></div>
              <style>{spinnerAnimation}</style>
              <p className="sb-loading-text">
                {isAssessmentGenerating ? "Generating assessment questions..." : "Loading assessment..."}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Show the same loading screen when assessment is generating even if page is loaded
  if (isAssessmentGenerating && !loading) {
    return (
      <div className="app-layout">
        <SideMenu />
        <div className={`learning-plan-main-content ${theme}`}>
          <div className="sb-loading-container">
            <div className="loading-icon-container">
              <div className="empty-state-icon">📝</div>
            </div>
            <div className="sb-loading-row">
              <div className="sb-loading-spinner"></div>
              <style>{spinnerAnimation}</style>
              <p className="sb-loading-text">Generating assessment questions...</p>
            </div>
            <p className="sb-loading-subtext">
              This may take a minute or two as we create personalized questions based on the lesson content.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <SideMenu />
      <div className={`learning-plan-main-content ${theme}`}>
        <div className={`lesson-assessment-container ${theme}`}>
          <div className="header-container">
            <h2>Lesson Assessment</h2>
            {!loading && score === null && (
              <button
                type="button"
                className="header-regenerate-btn"
                onClick={handleRegenerateClick}
                disabled={regenerating || isAssessmentGenerating}
              >
                {regenerating || isAssessmentGenerating ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg className="loading-spinner" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ width: '16px', height: '16px' }}>
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    </svg>
                    {regenerating ? "Regenerating..." : "Generating..."}
                  </span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg className="refresh-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ width: '16px', height: '16px' }}>
                      <path d="M17.65 6.35A8 8 0 1 0 19.73 14h-2.08A6 6 0 1 1 16 7.89V10l4-4-4-4v2.73z" fill="currentColor" />
                    </svg>
                    Regenerate Assessment
                  </span>
                )}
              </button>
            )}
          </div>
          <h3>{lesson?.title}</h3>

          {score !== null ? (
            <div className="assessment-results">
              <h3>Assessment Results</h3>
              <div className="score-display">
                <div className="score-circle">
                  <span className="score-value" data-score={score}>
                    {score}
                  </span>
                  <span className="score-percent">%</span>
                </div>
                <p className="score-message">
                  {score === 100
                    ? "Perfect score! You have mastered this lesson."
                    : score >= 80
                    ? "Excellent! You have a great understanding of this lesson."
                    : score >= 60
                    ? "Good job! You have a solid understanding of this lesson."
                    : "You might need to review this lesson again."}
                </p>
                {score >= 60 ? (
                  <p className="completed-score-message">
                    Lesson marked as completed! ✓
                  </p>
                ) : (
                  <p className="incomplete-score-message">
                    Score below 60%. Lesson not completed.
                  </p>
                )}
              </div>

              <div className="assessment-form-actions">
                <button
                  onClick={handleReturnToLesson}
                  className="btn-secondary btn-small"
                  style={{width: 'auto'}}
                >
                  Back to Lesson
                </button>
                <button
                  onClick={handleContinue}
                  className="roadmap-button"
                  disabled={isNextLessonContentGenerating || hasPaymentError}
                >
                  {isNextLessonContentGenerating ? (
                    <>
                      <div className="sb-loading-spinner" style={{marginRight: '8px', width: '16px', height: '16px'}}></div>
                      Preparing Next Lesson...
                    </>
                  ) : hasPaymentError ? (
                    'Upgrade Required'
                  ) : (
                    'Go to Next Lesson'
                  )}
                </button>
              </div>
              
              {/* Content Generation Status Indicator */}
              {showContentStatus && (
                <div className={`content-generation-status ${theme}`}>
                  <div className="status-icon"></div>
                  <span>Next lesson content is being prepared...</span>
                </div>
              )}
            </div>
          ) : (
            <>

              {isAssessmentGenerating ? (
                <div className="assessment-loading-container">
                  <div className="knowledge-assessment-loading-spinner"></div>
                  <style>{spinnerAnimation}</style>
                  <p className="assessment-loading-text">Generating assessment questions...</p>
                  <p className="assessment-loading-text small">This may take a minute or two as we create personalized questions based on the lesson content.</p>
                </div>
              ) : questions.length === 0 ? (
                <div className="empty-state">
                  <p>No assessment questions available.</p>
                  <button
                    onClick={handleReturnToLesson}
                    className="btn-secondary btn-small"
                    style={{width: 'auto'}}
                  >
                    Back to Lesson
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="assessment-form">
                  <p className="assessment-intro">
                    Let's test your understanding of this lesson. Answer the
                    following questions to the best of your ability.
                  </p>
                  {questions.map((question, index) => (
                    <div key={index} className="question-card">
                      <div className="question-header">
                        <div className="question-number">{index + 1}</div>
                        <div className="question-content">
                          {renderQuestion(question)}
                        </div>
                      </div>

                      {/* If question has options, show multiple choice */}
                      {typeof question === "object" &&
                      question.options &&
                      Array.isArray(question.options) ? (
                        <div className="options-list">
                          {question.options.map((option, optionIndex) => (
                            <label key={optionIndex} className="option-label">
                              <input
                                type="radio"
                                name={`question-${index}`}
                                value={option}
                                checked={answers[index] === option}
                                onChange={() =>
                                  handleAnswerChange(index, option)
                                }
                                required
                              />
                              {renderOption(option)}
                            </label>
                          ))}
                        </div>
                      ) : (
                        // If no options, show a text area for free-form answers
                        <textarea
                          value={answers[index] || ""}
                          onChange={(e) =>
                            handleAnswerChange(index, e.target.value)
                          }
                          placeholder="Your answer..."
                          rows={3}
                          required
                        />
                      )}
                    </div>
                  ))}

                  <div className="assessment-form-actions">
                    <button
                      type="button"
                      className="btn-secondary btn-small"
                      onClick={handleReturnToLesson}
                      disabled={isAssessmentGenerating}
                      style={{width: 'auto'}}
                    >
                      Back to Lesson
                    </button>
                    <button
                      type="submit"
                      className="roadmap-button"
                      disabled={submitting || isAssessmentGenerating}
                    >
                      {submitting ? "Submitting..." : "Submit Answers"}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}

          {/* Regenerate Warning Modal */}
          {showRegenerateModal && (
            <div className={`assessment-regenerate-modal show ${theme}`}>
              <div className={`assessment-modal-content ${theme}`}>
                <div className="assessment-modal-header">
                  <h3 className="assessment-modal-title">Warning: Regenerate Assessment</h3>
                <button 
                    className="assessment-modal-close" 
                  onClick={closeRegenerateModal}
                >
                  ×
                </button>
              </div>
                <div className="assessment-modal-body">
                <p><strong>Caution:</strong> Regenerating the assessment will:</p>
                <ul style={{paddingLeft: '20px', marginTop: '10px'}}>
                  <li>Create new questions</li>
                  <li>Reset your current answers</li>
                  <li>Require you to start over</li>
                </ul>
                <p style={{marginTop: '15px'}}>This action cannot be undone. Are you sure you want to continue?</p>
              </div>
                <div className="assessment-modal-footer">
                <button 
                    className="assessment-modal-cancel" 
                  onClick={closeRegenerateModal}
                >
                  Cancel
                </button>
                <button 
                    className="assessment-modal-confirm" 
                  onClick={handleConfirmRegenerate}
                >
                  Regenerate Anyway
                </button>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LessonAssessment;
